import { Box, Typography, Chip, Paper } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { Activity } from '../types/Activity';

interface EnhancedActivity extends Activity {
    sender?: string;
    recipients?: string;
    summary?: string;
    sentiment?: 'Positive' | 'Neutral' | 'Negative';
    detectedActionItems?: string[];
}

const getSentimentColor = (sentiment?: string) => {
    if (sentiment === 'Positive') return 'success';
    if (sentiment === 'Negative') return 'error';
    return 'default';
};

export default function IntelligentEmailActivity({ activity }: { activity: EnhancedActivity }) {
    // If there's no AI-enhanced data, don't render this component
    if (!activity.summary && !activity.sentiment) {
        return null;
    }

    // Extract sender name from email format "Name <email@example.com>"
    const getSenderName = (sender?: string) => {
        if (!sender) return activity.ownerName || 'Unknown';
        if (sender.includes('<')) {
            return sender.split('<')[0].trim();
        }
        return sender;
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">
                    {activity.subject || `Email from ${getSenderName(activity.sender)}`}
                </Typography>
                {activity.sentiment && (
                    <Chip 
                        label={activity.sentiment} 
                        color={getSentimentColor(activity.sentiment) as any} 
                        size="small" 
                    />
                )}
            </Box>
            {activity.summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {activity.summary}
                </Typography>
            )}
            {activity.detectedActionItems && activity.detectedActionItems.length > 0 && (
                <Box sx={{ mt: 2, borderTop: '1px solid #eee', pt: 1 }}>
                    <Typography variant="subtitle2">Action Items</Typography>
                    {activity.detectedActionItems.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <CheckCircleOutlineIcon color="action" sx={{ mr: 1 }} />
                            <Typography variant="body2">{item}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {new Date(activity.createdAt).toLocaleString()}
            </Typography>
        </Paper>
    );
} 